/**
 * Shared-event round math — standalone leaf module (no hono/drizzle). Both event
 * games run on TIME MATH, not a process: a round id is a pure function of the
 * clock, the outcome is HMAC(seed, roundId) — predetermined but unknowable (seed
 * is secret) and never revealed by the server before the round closes. No cron,
 * no websockets: every client syncs to serverNow and replays the same seeded
 * reveal at the same moment.
 *
 * draw.  — one round per day, reveal at 19:00 UTC. Pick 1 of 12; brag = call streak.
 * rocket. — one round per 10 minutes: board for 9, then everyone watches the SAME
 *           flight (the crash curve anchored to launch time) and cashes out solo.
 */

// --- draw ---

export const DRAW_SLOTS = 12;
export const DRAW_INTERVAL_S = 86_400;
/** Shift epoch so each round ENDS at 19:00 UTC (reveal time). */
export const DRAW_OFFSET_S = 19 * 3600;
/** Picks lock this long before the reveal. */
export const DRAW_LOCK_S = 60;

export function drawRoundAt(nowMs: number): number {
  return Math.floor((nowMs / 1000 - DRAW_OFFSET_S) / DRAW_INTERVAL_S);
}

/** When round r reveals (ms epoch). */
export function drawEndsAt(round: number): number {
  return ((round + 1) * DRAW_INTERVAL_S + DRAW_OFFSET_S) * 1000;
}

export function drawPicksLocked(nowMs: number, round: number): boolean {
  return nowMs >= drawEndsAt(round) - DRAW_LOCK_S * 1000;
}

/** Winning slot from the round's HMAC digest (hex). Uniform via 52-bit slice. */
export function drawOutcome(digestHex: string): number {
  const bits = Number.parseInt(digestHex.slice(0, 13), 16);
  return bits % DRAW_SLOTS;
}

// --- rocket ---

export const ROCKET_INTERVAL_S = 600;
/** Boarding occupies the first 9 minutes of a round; the flight gets the last one. */
export const ROCKET_BOARDING_S = 540;
/** A flight never outlives its minute (the curve hits absurd heights long before). */
export const ROCKET_MAX_FLIGHT_S = 55;

export type RocketPhase = "boarding" | "flying" | "landed";

export function rocketRoundAt(nowMs: number): number {
  return Math.floor(nowMs / 1000 / ROCKET_INTERVAL_S);
}

export function rocketLaunchAt(round: number): number {
  return (round * ROCKET_INTERVAL_S + ROCKET_BOARDING_S) * 1000;
}

export function rocketRoundStart(round: number): number {
  return round * ROCKET_INTERVAL_S * 1000;
}

/**
 * Phase of round r at nowMs. "landed" covers both post-crash and the slack after
 * ROCKET_MAX_FLIGHT_S; the next round's boarding starts on the next boundary.
 */
export function rocketPhase(
  nowMs: number,
  round: number,
  crashesAtMs: number,
): RocketPhase {
  const launch = rocketLaunchAt(round);
  if (nowMs < launch) return "boarding";
  if (nowMs < Math.min(crashesAtMs, launch + ROCKET_MAX_FLIGHT_S * 1000)) {
    return "flying";
  }
  return "landed";
}
